/**
 * The work replay's one request, and a strict reading of its answer. Errors
 * come back as one WorkReplayError whose `kind` the section switches on, so
 * nothing reads axios internals.
 */
import { isAxiosError } from 'axios';
import axios from '../../../axiosConfig';
import { readView } from './manifest';
import type { WorkReplayView } from './types';

export type WorkReplayErrorKind =
  /** No replay for this candidate on this job (or Rio's storage is off). */
  | 'none'
  /** The anonymised view: a replay can carry the candidate's voice and screen. */
  | 'masked'
  /** Rio is still saving it. */
  | 'saving'
  /** Anything else: Rio down, the network, a reply that isn't a replay. */
  | 'unavailable';

export class WorkReplayError extends Error {
  readonly kind: WorkReplayErrorKind;

  constructor(kind: WorkReplayErrorKind) {
    super(kind);
    this.name = 'WorkReplayError';
    this.kind = kind;
  }
}

export const toWorkReplayError = (error: unknown): WorkReplayError => {
  if (error instanceof WorkReplayError) return error;
  if (!isAxiosError(error)) return new WorkReplayError('unavailable');
  switch (error.response?.status) {
    case 404:
      return new WorkReplayError('none');
    case 403:
      return new WorkReplayError('masked');
    case 409:
      return new WorkReplayError('saving');
    default:
      return new WorkReplayError('unavailable');
  }
};

/** Rio's view function can start cold; the backend gives it 15 s, so this waits a little longer. */
const TIMEOUT_MS = 20_000;

export type FetchWorkReplay = (
  jobId: number,
  candidateId: number,
  assetId?: string,
) => Promise<WorkReplayView>;

/** GET /api/jobs/{jobId}/candidates/{candidateId}/work-replay[?assetId=]. */
export const fetchWorkReplay: FetchWorkReplay = async (
  jobId,
  candidateId,
  assetId,
) => {
  let data: unknown;
  try {
    data = (
      await axios.get(
        `/api/jobs/${jobId}/candidates/${candidateId}/work-replay`,
        {
          params: assetId ? { assetId } : undefined,
          timeout: TIMEOUT_MS,
        },
      )
    ).data;
  } catch (error) {
    throw toWorkReplayError(error);
  }
  const view = readView(data);
  if (!view) throw new WorkReplayError('unavailable');
  return view;
};
